import React, { forwardRef } from 'react';
import {
  backgroundColorVariants,
  type BackgroundVariants,
} from '../../utils/backgroundColor/backgroundColor';
import {
  paddingVariants,
  type PaddingVariants,
} from '../../utils/padding/padding';
import paddingStyles from '../../utils/padding/padding.module.css';
import styles from './Section.module.css';
import { cva } from 'class-variance-authority';
import '../../utils/padding/padding.module.css';
import { flexVariants, type FlexVariants } from '../../utils/flex/flex';
import flexStyles from '../../utils/flex/flex.module.css';
import {
  getBaseValueOrUndefined,
  generateResponsiveClasses,
  type WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';

const getResponsiveClasses = generateResponsiveClasses(styles);
const getResponsiveClassesPadding = generateResponsiveClasses(paddingStyles);
const getResponsiveClassesFlex = generateResponsiveClasses(flexStyles);

const sectionPaddingVariants = cva('', {
  variants: {
    paddingX: {
      default: styles['padding-x--default'],
    },
    paddingY: {
      default: styles['padding-y--default'],
    },
    paddingTop: {
      default: styles['padding-top--default'],
    },
    paddingRight: {
      default: styles['padding-right--default'],
    },
    paddingBottom: {
      default: styles['padding-bottom--default'],
    },
    paddingLeft: {
      default: styles['padding-left--default'],
    },
  },
});

type SectionPaddingValue = 'default' | PaddingVariants['paddingX'];
export type SectionPadding = {
  /**
   * Controls both left and right padding of the section
   */
  paddingX?: SectionPaddingValue;
  /**
   * Controls both top and bottom padding of the section
   */
  paddingY?: SectionPaddingValue;
  /**
   * Controls the top padding of the section (overridden by paddingY)
   */
  paddingTop?: SectionPaddingValue;
  /**
   * Controls the right padding of the section (overridden by paddingX)
   */
  paddingRight?: SectionPaddingValue;
  /**
   * Controls the bottom padding of the section (overridden by paddingY)
   */
  paddingBottom?: SectionPaddingValue;
  /**
   * Controls the left padding of the section (overridden by paddingX)
   */
  paddingLeft?: SectionPaddingValue;
};

// TODO: remove containerMaxWidth from here in future breaking change and move to Container only
export interface SectionBaseProps
  extends BackgroundVariants,
    SectionPadding,
    Pick<FlexVariants, 'flexDirection' | 'justifyContent' | 'alignItems'> {
  /**
   * The content of the section
   */
  children: React.ReactNode;
  /**
   * Controls the maximum width of the section container
   */
  containerMaxWidth?: 'full-width' | 'page';
  /**
   * The style of the section.
   */
  style?: React.CSSProperties;
  /**
   * The class name of the section.
   */
  className?: string;
  /**
   * Test identifier for testing purposes.
   * Defaults to "section" if not provided.
   */
  'data-testid'?: string;
}

type SectionResponsivePropKeys =
  | 'paddingTop'
  | 'paddingRight'
  | 'paddingBottom'
  | 'paddingLeft'
  | 'paddingX'
  | 'paddingY'
  | 'flexDirection'
  | 'justifyContent'
  | 'alignItems'
  | 'containerMaxWidth';

export type SectionProps = WithResponsiveProps<
  SectionBaseProps,
  SectionResponsivePropKeys
>;

const sectionContainer = cva('', {
  variants: {
    containerMaxWidth: {
      'full-width': styles['container--max-width-full-width'],
      page: styles['container--max-width-page'],
    },
  },
});

export interface ContainerProps {
  children: React.ReactNode;
  /**
   * The class name of the container.
   */
  className?: string;
  /**
   * The maximum width of the container.
   */
  containerMaxWidth?: 'full-width' | 'page';
  /**
   * Direction of the flex container.
   */
  flexDirection?: FlexVariants['flexDirection'];
  /**
   * Alignment along the main axis.
   */
  justifyContent?: FlexVariants['justifyContent'];
  /**
   * Alignment along the cross axis.
   */
  alignItems?: FlexVariants['alignItems'];
}

type ContainerResponsivePropKeys =
  | 'containerMaxWidth'
  | 'flexDirection'
  | 'justifyContent'
  | 'alignItems';

export type ContainerResponsiveProps = WithResponsiveProps<
  ContainerProps,
  ContainerResponsivePropKeys
>;

const Container = ({
  children,
  className,
  containerMaxWidth = 'page',
  flexDirection,
  justifyContent,
  alignItems,
}: ContainerResponsiveProps) => {
  const baseContainerMaxWidth =
    getBaseValueOrUndefined(containerMaxWidth) ?? 'page';
  const baseFlexDirection = getBaseValueOrUndefined(flexDirection);
  const baseJustifyContent = getBaseValueOrUndefined(justifyContent);
  const baseAlignItems = getBaseValueOrUndefined(alignItems);
  // Responsive classes for flex props
  const containerMaxWidthResponsiveClasses = getResponsiveClasses(
    containerMaxWidth,
    'container--max-width',
  );
  const flexDirectionResponsiveClasses = getResponsiveClassesFlex(
    flexDirection,
    'flex--direction',
  );
  const justifyContentResponsiveClasses = getResponsiveClassesFlex(
    justifyContent,
    'flex--justify-content',
  );
  const alignItemsResponsiveClasses = getResponsiveClassesFlex(
    alignItems,
    'flex--align-items',
  );

  const widthClassName =
    baseContainerMaxWidth === 'full-width'
      ? styles.fullWidth
      : styles.pageWidth;

  return (
    <div
      className={[
        sectionContainer({ containerMaxWidth: baseContainerMaxWidth }),
        styles.container,
        widthClassName,
        ...containerMaxWidthResponsiveClasses,
        flexVariants({
          display:
            baseFlexDirection || baseJustifyContent || alignItems
              ? 'flex'
              : undefined,
          flexDirection: baseFlexDirection,
          justifyContent: baseJustifyContent,
          alignItems: baseAlignItems,
        }),
        ...flexDirectionResponsiveClasses,
        ...justifyContentResponsiveClasses,
        ...alignItemsResponsiveClasses,
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
};

interface SectionComponent
  extends React.ForwardRefExoticComponent<
    SectionProps & React.RefAttributes<HTMLElement>
  > {
  Container: typeof Container;
}

// FIXME: Props are passed to container from section. Need to find a better way to handle this. Will be a breaking change.
const Section = forwardRef<HTMLElement, SectionProps>(
  (
    {
      children,
      containerMaxWidth = 'page',
      backgroundColor,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      paddingX,
      paddingY = 'default',
      style,
      className,
      flexDirection,
      justifyContent,
      alignItems,
      'data-testid': dataTestId = 'section',
    },
    ref,
  ) => {
    // Responsive base Props
    const basePaddingTop = getBaseValueOrUndefined(paddingTop);
    const basePaddingBottom = getBaseValueOrUndefined(paddingBottom);
    const basePaddingLeft = getBaseValueOrUndefined(paddingLeft);
    const basePaddingRight = getBaseValueOrUndefined(paddingRight);
    const basePaddingX = getBaseValueOrUndefined(paddingX);
    const basePaddingY = getBaseValueOrUndefined(paddingY);

    const baseFlexDirection = getBaseValueOrUndefined(flexDirection);
    const baseJustifyContent = getBaseValueOrUndefined(justifyContent);
    const baseAlignItems = getBaseValueOrUndefined(alignItems);

    // Responsive classes for padding props
    const paddingTopResponsiveClasses = getResponsiveClassesPadding(
      paddingTop,
      'padding-top',
    );
    const paddingXResponsiveClasses = getResponsiveClassesPadding(
      paddingX,
      'padding-x',
    );
    const paddingYResponsiveClasses = getResponsiveClassesPadding(
      paddingY,
      'padding-y',
    );
    const paddingBottomResponsiveClasses = getResponsiveClassesPadding(
      paddingBottom,
      'padding-bottom',
    );
    const paddingLeftResponsiveClasses = getResponsiveClassesPadding(
      paddingLeft,
      'padding-left',
    );
    const paddingRightResponsiveClasses = getResponsiveClassesPadding(
      paddingRight,
      'padding-right',
    );

    // Responsive classes for flex props
    const flexDirectionResponsiveClasses = getResponsiveClassesFlex(
      flexDirection,
      'flex-direction',
    );
    const justifyContentResponsiveClasses = getResponsiveClassesFlex(
      justifyContent,
      'flex-justify-content',
    );
    const alignItemsResponsiveClasses = getResponsiveClassesFlex(
      alignItems,
      'flex-align-items',
    );

    // To ensure that if any of the padding props are set, we don't apply the default padding class for paddingX and paddingY
    const hasPaddingTop = paddingTop != null;
    const hasPaddingBottom = paddingBottom != null;
    const hasPaddingLeft = paddingLeft != null;
    const hasPaddingRight = paddingRight != null;

    return (
      <section
        ref={ref}
        className={[
          styles.section,
          paddingVariants({
            paddingTop:
              basePaddingTop !== 'default' ? basePaddingTop : undefined,
            paddingBottom:
              basePaddingBottom !== 'default' ? basePaddingBottom : undefined,
            paddingLeft:
              basePaddingLeft !== 'default' ? basePaddingLeft : undefined,
            paddingRight:
              basePaddingRight !== 'default' ? basePaddingRight : undefined,
            paddingX: basePaddingX !== 'default' ? basePaddingX : undefined,
            paddingY: basePaddingY !== 'default' ? basePaddingY : undefined,
          }),
          sectionPaddingVariants({
            paddingTop: basePaddingTop === 'default' ? 'default' : undefined,
            paddingBottom:
              basePaddingBottom === 'default' ? 'default' : undefined,
            paddingLeft: basePaddingLeft === 'default' ? 'default' : undefined,
            paddingRight:
              basePaddingRight === 'default' ? 'default' : undefined,
            paddingX:
              basePaddingX === 'default' && !hasPaddingLeft && !hasPaddingRight
                ? 'default'
                : undefined,
            paddingY:
              basePaddingY === 'default' && !hasPaddingTop && !hasPaddingBottom
                ? 'default'
                : undefined,
          }),
          backgroundColorVariants({ backgroundColor }),
          flexVariants({
            flexDirection: baseFlexDirection,
            justifyContent: baseJustifyContent,
            alignItems: baseAlignItems,
          }),
          ...paddingTopResponsiveClasses,
          ...paddingRightResponsiveClasses,
          ...paddingBottomResponsiveClasses,
          ...paddingLeftResponsiveClasses,
          ...paddingXResponsiveClasses,
          ...paddingYResponsiveClasses,
          ...flexDirectionResponsiveClasses,
          ...justifyContentResponsiveClasses,
          ...alignItemsResponsiveClasses,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        style={style}
        data-testid={dataTestId}
      >
        {React.Children.map(children, (child) => {
          if (
            React.isValidElement<ContainerResponsiveProps>(child) &&
            child.type === Container
          ) {
            return React.cloneElement(child, {
              ...child.props,
              containerMaxWidth:
                child.props.containerMaxWidth ?? containerMaxWidth,
              flexDirection: child.props.flexDirection ?? flexDirection,
              justifyContent: child.props.justifyContent ?? justifyContent,
              alignItems: child.props.alignItems ?? alignItems,
            });
          }
          return child;
        })}
      </section>
    );
  },
) as SectionComponent;

Section.displayName = 'Section';
Section.Container = Container;
Container.displayName = 'Section.Container';

export default Section;
